import React, { useEffect, useMemo, useRef, useState } from 'react';
import { FiBookOpen, FiPlayCircle, FiStar, FiX, FiSearch, FiChevronRight } from 'react-icons/fi';
import { getApiBaseUrl } from '../../utils/apiBase';
import authService from '../../utils/auth.js';
import './help.css';

export const DEMO_VIDEO_URL = '/assets/help/novabotx-demo.mp4';

const TABS = [
  { id: 'topics', label: 'Help topics', icon: FiBookOpen },
  { id: 'demo', label: 'Demo video', icon: FiPlayCircle },
  { id: 'whats-new', label: "What's new", icon: FiStar }
];

/**
 * In-app help center: searchable guide topics (answered in the chat), the demo video,
 * and what's new. Everything stays inside NovaBotX; no files or emails needed.
 */
export default function HelpCenter({ tab = 'topics', onTabChange, onAsk, onClose }) {
  const [topics, setTopics] = useState([]);
  const [whatsNew, setWhatsNew] = useState([]);
  const [loadError, setLoadError] = useState(false);
  const [query, setQuery] = useState('');
  const searchRef = useRef(null);
  const isAdmin = authService.isAdmin();

  useEffect(() => {
    let cancelled = false;
    const base = getApiBaseUrl();
    Promise.all([
      fetch(`${base}/api/help/topics`).then((r) => r.json()),
      fetch(`${base}/api/help/whats-new`).then((r) => r.json())
    ])
      .then(([topicData, newsData]) => {
        if (cancelled) return;
        setTopics(topicData.topics || []);
        setWhatsNew(newsData.sections || []);
      })
      .catch(() => !cancelled && setLoadError(true));
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    const onKey = (e) => e.key === 'Escape' && onClose();
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  useEffect(() => {
    if (tab === 'topics') searchRef.current?.focus();
  }, [tab]);

  const groups = useMemo(() => {
    const q = query.trim().toLowerCase();
    const visible = topics.filter((t) =>
      t.title.endsWith('?') &&
      (isAdmin || !/admin/i.test(t.chapter)) &&
      !/about this release|what's new/i.test(t.chapter) &&
      (!q || t.title.toLowerCase().includes(q) || t.chapter.toLowerCase().includes(q))
    );
    const byChapter = new Map();
    visible.forEach((t) => {
      if (!byChapter.has(t.chapter)) byChapter.set(t.chapter, []);
      byChapter.get(t.chapter).push(t);
    });
    return [...byChapter.entries()];
  }, [topics, query, isAdmin]);

  const ask = (question) => {
    onAsk(question);
    onClose();
  };

  return (
    <div className="nb-help-overlay" onClick={onClose}>
      <div
        className="nb-help-center"
        role="dialog"
        aria-modal="true"
        aria-label="NovaBotX help"
        onClick={(e) => e.stopPropagation()}
      >
        <header className="nb-help-head">
          <div>
            <span className="nb-help-kicker">Help</span>
            <h3>How can we help?</h3>
          </div>
          <button type="button" className="nb-help-close" onClick={onClose} aria-label="Close help">
            <FiX />
          </button>
        </header>

        <nav className="nb-help-tabs" role="tablist">
          {TABS.map(({ id, label, icon: Icon }) => (
            <button
              key={id}
              type="button"
              role="tab"
              aria-selected={tab === id}
              className={`nb-help-tab ${tab === id ? 'is-active' : ''}`}
              onClick={() => onTabChange(id)}
            >
              <Icon aria-hidden="true" /> {label}
            </button>
          ))}
        </nav>

        <div className="nb-help-body">
          {tab === 'topics' && (
            <>
              <label className="nb-help-search">
                <FiSearch aria-hidden="true" />
                <input
                  ref={searchRef}
                  type="search"
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  placeholder="Search help, e.g. sign a PDF"
                  aria-label="Search help topics"
                />
              </label>
              {query.trim() && (
                <button type="button" className="nb-help-ask" onClick={() => ask(query.trim())}>
                  Ask NovaBotX: “{query.trim()}” <FiChevronRight aria-hidden="true" />
                </button>
              )}
              {loadError && <p className="nb-help-empty">Help topics couldn't load. You can still ask your question in the chat.</p>}
              {!loadError && groups.length === 0 && topics.length > 0 && (
                <p className="nb-help-empty">No topics match. Try other words, or ask NovaBotX directly.</p>
              )}
              {groups.map(([chapter, items]) => (
                <section key={chapter} className="nb-help-group">
                  <h4>{chapter}</h4>
                  <ul>
                    {items.map((t) => (
                      <li key={t.id}>
                        <button type="button" onClick={() => ask(t.title)}>
                          <span>{t.title}</span>
                          <FiChevronRight aria-hidden="true" />
                        </button>
                      </li>
                    ))}
                  </ul>
                </section>
              ))}
            </>
          )}

          {tab === 'demo' && (
            <div className="nb-help-demo">
              <video src={DEMO_VIDEO_URL} controls autoPlay playsInline preload="metadata" />
              <p>A 40-second look at chatting, My Apps and the office tools.</p>
            </div>
          )}

          {tab === 'whats-new' && (
            <div className="nb-help-news">
              {whatsNew.map((section) => (
                <section key={section.title}>
                  <h4>{section.title}</h4>
                  <ul>
                    {section.items.map((item) => <li key={item}>{item}</li>)}
                  </ul>
                </section>
              ))}
              {!whatsNew.length && !loadError && <p className="nb-help-empty">Loading…</p>}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
